import { componentGroupCls } from "@faire/slate/lib/tracking";
import { IAccordionGroupProps } from "@faire/slate/private/accordion/types";
import type { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import type { ServerSideLocalMsg } from "@faire/web/ui/lib/localization/ServerSideLocalMsg";
import React from "react";

import { AccordionItem } from "./AccordionItem";

type IProps = {
  /**
   * The props of all the accordion items in the accordion group.
   */
  items: Array<
    Omit<React.ComponentProps<typeof AccordionItem>, "expanded" | "onToggle">
  >;

  /**
   * Optional footer text to display at the bottom of the accordion group.
   */
  footer?:
    | string
    | React.ReactElement<typeof StrictLocalMsg>
    | React.ReactElement<typeof ServerSideLocalMsg>;

  /**
   * **web**
   * The index of the AccordionItem that is initially expanded.
   */
  initiallyExpandedIndex?: number;

  /**
   * **web**
   * Callback function that is triggered when an AccordionItem is clicked
   *
   * @param index - The index of the AccordionItem that was clicked
   * @param e - The toggle event object, see https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/toggle_event
   */
  onToggle?: (index: number, e: React.ToggleEvent<HTMLDetailsElement>) => void;

  // DO NOT ADD any new prop without aligning with Slate team first
} & IAccordionGroupProps;

/**
 * Accordions help users understand detailed information via an expandable structure.
 *
 * See [migration guide](migration-guide.mdx) for more information.
 *
 * @trackcomponent
 * @version 2.0.0
 */
export const AccordionGroup: React.FC<IProps> = ({
  footer,
  items,
  initiallyExpandedIndex,
  onToggle,
  ...props
}) => (
  <section {...props} className={componentGroupCls("fs-accordion-group")}>
    {items.map((item, index) => (
      <AccordionItem
        key={`accordion-item-${index}-${item.id ?? item.label}`}
        {...item}
        expanded={initiallyExpandedIndex === index}
        onToggle={(e) => onToggle?.(index, e)}
      />
    ))}
    {footer ? <p className="fs-accordion-group__footer">{footer}</p> : null}
  </section>
);
